// Which of the walker's waypoints go on the map.
//
// "Show waypoints on map" is the master switch and still means every waypoint.
// Each waypoint group also has its own switch, so a walker can show just one or
// two groups without turning everything on. With the master off, the map shows
// the waypoints of every switched-on group (a waypoint in two groups once).
//
// Group choices are remembered per device by group clientId, and survive the
// master being switched on and off. iOS and Android follow the same rules.

export const VISIBLE_WAYPOINT_GROUPS_STORAGE_KEY = 'visibleWaypointGroupClientIds';
export const WAYPOINT_GROUP_VISIBILITY_EVENT = 'waypoint_group_visibility_toggled';

interface HasClientId { clientId?: string | null }

export interface WaypointGroupLike extends HasClientId {
  waypoints?: HasClientId[] | null;
}

/**
 * The waypoints to draw. `allWaypoints` is the user's live (non-deleted) list and
 * is what gets returned, so a stale group membership can't bring back a deleted
 * waypoint. Order follows `allWaypoints`.
 */
export function visibleWaypoints<T extends HasClientId>(
  allWaypoints: T[],
  showAll: boolean,
  visibleGroupClientIds: string[],
  groups: WaypointGroupLike[],
): T[] {
  if (showAll) return allWaypoints;
  if (visibleGroupClientIds.length === 0) return [];

  const switchedOn = new Set(visibleGroupClientIds);
  const members = new Set<string>();
  groups.forEach((group) => {
    if (!group.clientId || !switchedOn.has(group.clientId)) return;
    (group.waypoints ?? []).forEach((w) => { if (w?.clientId) members.add(w.clientId); });
  });
  return allWaypoints.filter((w) => !!w.clientId && members.has(w.clientId));
}

/** True when the map needs group membership to know what to draw. */
export function dependsOnGroups(showAll: boolean, visibleGroupClientIds: string[]): boolean {
  return !showAll && visibleGroupClientIds.length > 0;
}

/** The set after one group's switch is flipped to `visible`. */
export function setGroupVisible(visibleGroupClientIds: string[], clientId: string, visible: boolean): string[] {
  const without = visibleGroupClientIds.filter((id) => id !== clientId);
  return visible ? [...without, clientId] : without;
}

/** Drops ids for groups that no longer exist (deleted here or on another device). */
export function pruneVisibleGroups(visibleGroupClientIds: string[], groups: HasClientId[]): string[] {
  const existing = new Set(groups.map((g) => g.clientId).filter(Boolean) as string[]);
  return visibleGroupClientIds.filter((id) => existing.has(id));
}

/** Reads the stored set. Anything unreadable is treated as "no groups on". */
export function parseVisibleGroups(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.filter((id): id is string => typeof id === 'string' && id.length > 0))];
  } catch {
    return [];
  }
}

export function loadVisibleGroups(): string[] {
  try {
    return parseVisibleGroups(window.localStorage.getItem(VISIBLE_WAYPOINT_GROUPS_STORAGE_KEY));
  } catch {
    return [];
  }
}

export function saveVisibleGroups(visibleGroupClientIds: string[]): void {
  try {
    window.localStorage.setItem(VISIBLE_WAYPOINT_GROUPS_STORAGE_KEY, JSON.stringify(visibleGroupClientIds));
  } catch {}
}

export interface WaypointGroupVisibilityEventProps {
  visible: boolean;
  groups_visible_count: number;
  master_on: boolean;
}

export function groupVisibilityEventProps(
  visible: boolean,
  visibleGroupClientIds: string[],
  showAll: boolean,
): WaypointGroupVisibilityEventProps {
  return { visible, groups_visible_count: visibleGroupClientIds.length, master_on: showAll };
}

/** Fire-and-forget; a missing analytics shim must never break the switch. */
export function trackGroupVisibilityToggled(props: WaypointGroupVisibilityEventProps): void {
  try {
    (window as any).mixpanel?.track(WAYPOINT_GROUP_VISIBILITY_EVENT, props);
  } catch {}
}
